import React, {SyntheticEvent, useEffect, useRef, forwardRef, useState, Fragment} from "react";
import {Button, Icon, Modal, Tabs, Card, Row, Col, Divider, Tooltip} from "antd";
import {Image} from "@models/image";
const {TabPane} = Tabs;
import imageApi from "@api/image";
import {PaginationDefaultParams} from "../Paginator";
import SearchInput from "@components/custom/SearchInput";
import {FormattedMessage} from 'react-intl';
import {ConnectedWrappedImageForm as ImageForm} from '@components/images/Form';
import ImagesItem from "../../images/Item";
import './index.scss'


type Props = {
  onSelectImage: (image: Image) => void;
  disableImagePicker: boolean;
  selectedImage?:Image
};


type ImagePickerData = {
  count: number,
  images: Image[]
}

const defaultData: ImagePickerData = {
  count: 0,
  images: []
};

const defaultRequestParams = {
  ...PaginationDefaultParams,
  query: ""
};


/**
 * state.visible: show/hide modal
 * Modal should NOT destroyed on hide
 * state.loading: Just in case of upstate.loading image
 * isFetched: check is the first state.loading of image or not : (false: should fetch image on show modal)
 * @param props
 * @constructor
 */


function ImagePickerModal(props: Props, ref) {
  const [state, setState] = useState({
    visible: false,
    data: defaultData,
    visibleUploadBtn: false,
    loading: false,
    isFetched: false,
    requestParams: {offset : 0 , count: 0},
    selectedImage : props.selectedImage
  });
  const offsetRef = useRef<typeof state.requestParams.offset>(state.requestParams.offset);


  useEffect(
    () => {
      setState({
        ...state,
        selectedImage : props.selectedImage
      })
    },
    [props.selectedImage]
  );

  const uploadImageFormRef = useRef();

  useEffect(() => {
    if (state.visible && !state.loading ) {
      if(offsetRef?.current == 0 || state.requestParams?.offset > offsetRef?.current){
        offsetRef.current = state.requestParams?.offset
        fetchImages();
      }

    }
  }, [state.requestParams]);

  const fetchImages = async () => {
    setState({...state, loading: true})
    imageApi.getList({
        params: state.requestParams
      })
      .then((resp) => {
        setState({
          ...state,
          data:{
            images: [...state.data.images, ...resp.data],
            count: resp.meta.total_count,
          },
          isFetched: true,
          loading: false
        })
      })
      .catch(() => {
        setState({
          ...state,
          loading: false
        })
      })
  };

  const selectImage = (image?: Image) => {
    setState({
      ...state,
      visible: false,
      selectedImage: image
    });
    props.onSelectImage(image);
  };

  const searchImages = (filter) => {
    setState({
      ...state,
      data: {
        images: [],
        count: 0
      },
      requestParams: {
        ...filter,
        ...PaginationDefaultParams
      }
    })
  };

  let offset : typeof  state.requestParams.offset= 20;
  const onScroll = (e: SyntheticEvent) => {
    e.persist();
    const {images, count} = state.data;

    let target = e.target;
    if (target.scrollTop + target.offsetHeight + 30 >= target.scrollHeight) {
      if (count > images.length) {
        let offset_tmp = state.requestParams.offset + 20;
        offset = state.requestParams.offset + offset;
        if (offset_tmp == offset) {
          setState({
            ...state,
            requestParams: {
              ...state.requestParams,
              offset: offset_tmp,
              count: 20
            }
          })
          offset = offset_tmp + offset;
        }
      }
    }
  };

  /**
   * Reset filter
   */
  const showModal = (e) => {
    if (( e && e.target.closest(".remove-image") ) || props.disableImagePicker) {
      return false;
    }

    setState({
      ...state,
      loading: false,
      visible: true,
      requestParams : !state.isFetched ? defaultRequestParams : state.requestParams
    })
  };


  /**
   * Call uplaod method of image form
   * Success callback
   */
  const uploadImage = () => {
    if (uploadImageFormRef.current.getValues()) {
      setState({
        ...state,
        loading: true
      });
      uploadImageFormRef.current.upload(selectImage);
    }
  };

  const removeImage = (e) => {
    selectImage(null);
    e.preventDefault()
  };

  return (
    <div>
      <div className={'image-box-wrapper'} onClick={showModal}>
        {props.children}

        {state.selectedImage && (
          <Tooltip title={<FormattedMessage id="app.remove_image" defaultMessage={`Remove Image`}/>}>
            <Icon type="close" className={'remove-image'} onClick={removeImage}/>
          </Tooltip>
        )}

      </div>

      <Modal desabled={true}
             width={800}
             visible={state.visible}
             closable={false}
             destroyOnClose={false}
             className={'image-picker-modal'}
             footer={(
               <Fragment>
                 <Button onClick={() => setState({
                   ...state,
                   visible: false
                 })}
                         disabled={state.loading}>
                   <FormattedMessage id="app.cancel" defaultMessage={`Cancel`}/>
                 </Button>
                 {state.visibleUploadBtn && (
                   <Button type={`primary`}
                           onClick={() => uploadImage()}
                           loading={state.loading}>
                     <FormattedMessage id="app.save" defaultMessage={`Save`}/>
                   </Button>
                 )}
               </Fragment>
             )}
      >
        <Tabs defaultActiveKey="1"
              onChange={(activeTab) => {
                const visibleUploadBtn = activeTab == 2;
                setState({
                  ...state,
                  visibleUploadBtn: visibleUploadBtn
                })
              }}
              tabPosition={'top'}>
          <TabPane tab={<FormattedMessage id="app.select_image" defaultMessage={`Select Image`}/>} key={1}>
            <Row className={`search-box`}>
              <Col sm={12}>
                <SearchInput search={searchImages}/>
              </Col>
              <Col></Col>
            </Row>

            <Row gutter={12} className={'list-wrapper'} onScroll={(e) => onScroll(e)}>
              {state.data.images.map((image, index) => (
                <ImagesItem
                  key={`${image}_${index}`}
                  image={image}
                  canEdit={false}
                  canDelete={false}
                  onClick={() => selectImage(image)}/>
              ))}
            </Row>
          </TabPane>

          <TabPane tab={<FormattedMessage id="app.upload_new_image" defaultMessage={`Upload new image`}/>}
                   key={2}>
            <div className={`form-modal`}>
              <ImageForm wrappedComponentRef={uploadImageFormRef}/>
            </div>
          </TabPane>
        </Tabs>
      </Modal>
    </div>
  );
}

export default forwardRef(ImagePickerModal);


